import moment from "moment";
import { checkFormulaSyntax, findMatchingParen } from "services/utils/formulaUtils";

// Plain helpers shared by the statutory configuration screens (PF, ESI,
// later PT): versions by month and the formula checks. No screen code here.

export const VERSION_STATUS_COLORS = { Active: "#12B76A", Scheduled: "#2563EB", Draft: "#F0954A", Superseded: "#6B7280" };
export const ACTIVE_STATUS_COLORS = { Active: "#12B76A", Inactive: "#6B7280" };

export const formatDay = (value) => (value ? moment(value).format("DD MMM YYYY") : "–");
export const formatMonth = (value) => (value ? moment(value).format("MMM YYYY") : "–");
export const versionRange = (version) =>
  `${formatDay(version.effective_from)} → ${version.effective_to ? formatDay(version.effective_to) : "onwards"}`;

export const activeVersion = (versions) =>
  versions.find((version) => version.status === "Active") || versions[versions.length - 1] || null;

// Per-site items (LWF, PT): site = { versions }. Drafts never count.
export const savedVersions = (site) => (site?.versions || []).filter((version) => version.status !== "Draft");

// The saved version payroll uses on a date, and a saved one starting later
// ("Active + Version n from …" on the sites lists).
export const versionInForce = (site, on = moment().format("YYYY-MM-DD")) =>
  savedVersions(site)
    .filter((version) => version.effective_from <= on && (!version.effective_to || version.effective_to >= on))
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] || null;

export const upcomingVersion = (site, on = moment().format("YYYY-MM-DD")) =>
  savedVersions(site)
    .filter((version) => version.effective_from > on)
    .sort((a, b) => a.effective_from.localeCompare(b.effective_from))[0] || null;

// A version used by a payroll can't be changed; changes go into a new one.
export const isLocked = (version) => Boolean(version.is_locked) || version.status === "Superseded" || (version.used_by || []).length > 0;

// Create New Version: the active version copied as a Draft from the next
// 1st of a month after it starts.
export const draftFrom = (versions) => {
  const base = activeVersion(versions);
  const nextNumber = Math.max(0, ...versions.map((version) => version.id)) + 1;
  const after = moment.max(moment().add(1, "month").startOf("month"), moment(base.effective_from).add(1, "month").startOf("month"));
  return {
    ...JSON.parse(JSON.stringify(base)),
    id: nextNumber,
    name: `Version ${nextNumber}`,
    effective_from: after.format("YYYY-MM-DD"),
    effective_to: "",
    status: "Draft",
    description: "",
    used_by: [],
    created_by: "",
    created_at: "",
  };
};

// Name and effective date: a version starts on the 1st of a month and a
// draft must start after the version in force.
export const validateVersionDetails = (form, versions) => {
  const errors = {};
  if (!String(form.name || "").trim()) errors.name = "Enter a version name";
  if (!form.effective_from) errors.effective_from = "Pick the month it starts from";
  else if (moment(form.effective_from).date() !== 1) errors.effective_from = "Must be the 1st of a month";
  else if (form.status === "Draft") {
    const overlap = versions.some(
      (version) => version.id !== form.id && version.status !== "Draft" && !moment(form.effective_from).isAfter(moment(version.effective_from)),
    );
    if (overlap) errors.effective_from = "Must start after the current version's start month";
  }
  return errors;
};

export const numberOrNaN = (value) => (value === "" || value == null ? NaN : Number(value));

// ---- Formulas ----

// The formula reader knows CEIL / FLOOR, not ROUNDUP / ROUNDDOWN. The amount
// is rounded to paise first so 18,000 × 0.75% (134.99999… in floating point)
// rounds up to 135, not 136. The screens show × ÷ ≤ ≥; the reader wants
// * / <= >=.
const wrapRounding = (text, name, target) => {
  const pattern = new RegExp(`\\b${name}\\(`, "i");
  let out = text;
  let match = pattern.exec(out);
  while (match) {
    const open = match.index + name.length;
    const close = findMatchingParen(out, open);
    if (close < 0) return out.replace(pattern, `${target}(`);
    out = `${out.slice(0, match.index)}${target}(ROUND(${out.slice(open + 1, close)}, 2))${out.slice(close + 1)}`;
    match = pattern.exec(out);
  }
  return out;
};

export const toEngineFormula = (formula) =>
  wrapRounding(
    wrapRounding(String(formula || "").replace(/×/g, "*").replace(/÷/g, "/").replace(/≤/g, "<=").replace(/≥/g, ">="), "ROUNDUP", "CEIL"),
    "ROUNDDOWN",
    "FLOOR",
  );

export const toDisplayFormula = (formula) =>
  String(formula || "").replace(/\*/g, "×").replace(/<=/g, "≤").replace(/>=/g, "≥");

// Codes a formula reads (upper case), [] when it doesn't parse.
export const formulaReads = (formula) => {
  const check = checkFormulaSyntax(toEngineFormula(formula));
  return check.valid ? [...new Set(check.codes.map((code) => code.toUpperCase()))] : [];
};

const findCycle = (components, startCode, formulaOf) => {
  const byCode = Object.fromEntries(components.map((component) => [component.code, component]));
  const seen = new Set();
  const walk = (code, path) => {
    if (path.includes(code)) return [...path, code];
    if (seen.has(code) || !byCode[code]) return null;
    seen.add(code);
    for (const next of formulaReads(formulaOf(byCode[code]))) {
      const found = walk(next, [...path, code]);
      if (found) return found;
    }
    return null;
  };
  return walk(startCode, []);
};

// { valid, message } for a formula editor: empty, too long, bad syntax,
// unknown codes and circular references are refused.
// allowed: codes the formula may read. formulaOf(component): the formula a
// component actually runs (defaults to component.formula).
export const checkStatutoryFormula = (formula, { allowed, components, own, formulaOf = (component) => component.formula }) => {
  const text = String(formula || "").trim();
  if (!text) return { valid: false, message: "Enter a formula" };
  if (text.length > 500) return { valid: false, message: "500 characters at most" };
  const check = checkFormulaSyntax(toEngineFormula(text));
  if (!check.valid) return { valid: false, message: check.message || "Check the formula" };
  const allowedSet = new Set(allowed.map((code) => code.toUpperCase()));
  const unknown = [...new Set(check.codes.map((code) => code.toUpperCase()))].filter((code) => !allowedSet.has(code));
  if (unknown.length) return { valid: false, message: `Unknown code: ${unknown.join(", ")}` };
  const withThis = [...components.filter((component) => component.code !== own.code), { ...own, formula: text, __formula: text }];
  const cycle = findCycle(withThis, own.code, (component) => (component.__formula != null ? component.__formula : formulaOf(component)));
  if (cycle) return { valid: false, message: `Circular reference: ${cycle.join(" → ")}` };
  return { valid: true, message: "Formula is valid" };
};
